/**
 * A modal dialog, restyled from Radix.
 *
 * Radix supplies the parts that are easy to get wrong and invisible when they
 * are: focus trapping, restoring focus to the trigger on close, `aria-modal`,
 * Escape handling, and marking the rest of the page inert. Reimplementing that
 * to save a dependency means reimplementing the bugs.
 *
 * A title is required rather than optional — a dialog with no accessible name
 * is announced as "dialog" and nothing else.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 5.6
 */
import * as RadixDialog from '@radix-ui/react-dialog';

import { cn } from '../../lib/cn';

export interface DialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The accessible name. Required — see the note above. */
  title: string;
  /**
   * Announced with the title, for context a title cannot carry alone.
   *
   * Explicitly `| undefined`: a caller computing it conditionally has nothing
   * to pass when there is no context, and under
   * `exactOptionalPropertyTypes` an omitted-only optional would reject that.
   */
  description?: string | undefined;
  children: React.ReactNode;
  /** Footer actions, kept out of the scrolling body. */
  footer?: React.ReactNode;
  className?: string;
}

export function Dialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  className,
}: DialogProps): React.JSX.Element {
  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="bg-navy/40 fixed inset-0 z-50" />

        {/* Centred on wide screens, bottom-anchored on narrow ones: a sheet
            rising from the bottom keeps its actions within thumb reach
            (plan §7.3). */}
        <RadixDialog.Content
          className={cn(
            'fixed z-50 flex flex-col bg-white shadow-md',
            /* Narrow: a sheet from the bottom, so its actions stay within
               thumb reach (plan §7.3). */
            'inset-x-0 bottom-0 max-h-[90dvh] rounded-t-lg',
            /* Wide: centred. `right-auto` is set explicitly — overriding only
               `left` leaves `right: 0` from `inset-x-0` in force, which pins
               the box to the left edge instead of centring it. */
            'sm:top-1/2 sm:right-auto sm:bottom-auto sm:left-1/2',
            'sm:w-full sm:max-w-lg sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-lg',
            className,
          )}
        >
          <div className="border-gray-100 flex flex-col gap-1 border-b p-4">
            <RadixDialog.Title className="text-lg">{title}</RadixDialog.Title>
            {description === undefined ? null : (
              <RadixDialog.Description className="text-body text-sm">
                {description}
              </RadixDialog.Description>
            )}
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto p-4">{children}</div>

          {footer === undefined ? null : (
            <div className="border-gray-100 flex justify-end gap-2 border-t p-4">
              {footer}
            </div>
          )}
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}
